import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Button,
  Typography,
  Box,
  CssBaseline,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";
import MainLayout from '../../components/layouts/MainLayout';
import AuthHttpService from '../../api/AuthHttpService';
import StorageService from '../../services/StorageService';
import LoadingOverlay from '../../components/LoadingOverlay';

function Login() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkExistingAuth = async () => {
      const auth = StorageService.getAuth();
      
      if (!auth?.token) {
        return;
      }

      try {
        await AuthHttpService.checkLoggedUser(auth.token);
        if (auth?.is_admin) {
          navigate('/newhome');
        } else {
          navigate('/user-home');
        }
      } catch (error) {}
    };

    checkExistingAuth();
  }, [navigate]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !code) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    try {
      setLoading(true);
      const auth = await AuthHttpService.login(email, code);

      StorageService.saveAuth(auth);
      if (auth?.is_admin) {
        navigate('/newhome');
      } else {
        navigate('/user-home');
      }
    } catch (error) {
      setError(error.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!email) {
      setError('Por favor, preencha o email antes de enviar o código');
      return;
    }

    try {
      setLoading(true);
      await AuthHttpService.sendEmailCode(email);
      setSuccess('Um código foi enviado para o seu email');
      setTimer(60);
    } catch (error) {
      setError(error.message || 'Erro ao enviar o código');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setError('');
  };

  const handleCloseSuccess = () => {
    setSuccess('');
  };

  return (
    <MainLayout>
      <LoadingOverlay open={loading} />
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Container
        component="main"
        sx={{
          width: "460px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <CssBaseline />
        
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "white",
            padding: "32px",
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Login
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="code"
                label="Código"
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                sx={{ mb: 0 }}
              />
              <Button
                onClick={handleSendCode}
                disabled={timer > 0}
                sx={{
                  mt: 2,
                  color: timer > 0 ? '#9e9e9e' : '#FF6B00',
                  border: `1px solid ${timer > 0 ? '#9e9e9e' : '#FF6B00'}`,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: timer > 0 ? 'transparent' : 'rgba(255, 107, 0, 0.04)',
                    border: `1px solid ${timer > 0 ? '#9e9e9e' : '#FF6B00'}`,
                  },
                  '&.Mui-disabled': {
                    color: '#9e9e9e',
                    border: '1px solid #9e9e9e',
                  }
                }}
              >
                {timer > 0 ? `Aguarde ${timer}s` : 'Enviar código'}
              </Button>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 1,
                mb: 3,
                py: 1.5,
                backgroundColor: "#FF6B00",
                '&:hover': {
                  backgroundColor: "#e65100",
                },
              }}
            >
              Entrar
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Não tem uma conta?{' '}
                <Link
                  to="/register-step-one"
                  style={{
                    color: '#FF6B00',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Cadastre-se
                </Link>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Esqueceu a senha do aplicativo?{' '}
                <Link
                  to="/reset_password"
                  style={{
                    color: '#FF6B00',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Recuperar senha
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </MainLayout>
  );
}

export default Login;
